import { supabase } from "../lib/supabase";

const DEFAULT_LEAD_SOURCE = "website"; 

function normalizePhone(phone) {
  return String(phone || "").replace(/\s/g, "").trim();
}

function safeTrim(v) {
  return typeof v === "string" ? v.trim() : v;
}

// Normaliza el payload que le mandas a la Edge Function
function normalizeLeadPayload(leadData) {
  return {
    nombre: safeTrim(leadData?.nombre) || "",
    email: safeTrim(leadData?.email) || "",
    telefono: normalizePhone(leadData?.telefono),
    timeframe: leadData?.timeframe || leadData?.interes || leadData?.plazo || "",
    mensaje: leadData?.mensaje || leadData?.message || "",
    presupuesto: leadData?.presupuesto ?? null,
    proyectoInteres:
      leadData?.proyectoInteres || leadData?.proyecto_interes || "Residencial 3 Volcanes",
    fuente: leadData?.fuente || DEFAULT_LEAD_SOURCE,
  };
}

async function readEdgeErrorBody(err) {
  const res = err?.context;
  if (res && typeof res.text === "function") {
    try {
      return await res.text();
    } catch (_) {}
  }
  return err?.message || String(err);
}

export const crmService = {
  async getLeads(advisorId) {
    try {
      const { data, error } = await supabase
        ?.from("crm_leads")
        ?.select("*")
        ?.eq("advisor_id", advisorId)
        ?.order("created_at", { ascending: false });

      if (error) throw error;

      return data?.map((lead) => ({
        id: lead?.id,
        advisorId: lead?.advisor_id,
        nombre: lead?.nombre,
        email: lead?.email,
        telefono: lead?.telefono,
        fuente: lead?.fuente,
        proyectoInteres: lead?.proyecto_interes,
        presupuesto: lead?.presupuesto,
        stage: lead?.stage,
        nivelInteres: lead?.nivel_interes,
        ultimaActividad: lead?.ultima_actividad,
        proximoSeguimiento: lead?.proximo_seguimiento,
        notas: lead?.notas,
        createdAt: lead?.created_at,
        updatedAt: lead?.updated_at,
      }));
    } catch (error) {
      console.error("Error fetching leads:", error);
      throw error;
    }
  },
  async createLead(leadData, advisorId) {
    try {
      const { data, error } = await supabase?.from("crm_leads")?.insert({
        advisor_id: advisorId,
        nombre: leadData?.nombre,
        email: leadData?.email,
        telefono: leadData?.telefono,
        fuente: leadData?.fuente,
        proyecto_interes: leadData?.proyectoInteres,
        presupuesto: leadData?.presupuesto,
        stage: leadData?.stage || "new",
        nivel_interes: leadData?.nivelInteres || "warm",
        proximo_seguimiento: leadData?.proximoSeguimiento,
        notas: leadData?.notas,
      })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        advisorId: data?.advisor_id,
        nombre: data?.nombre,
        email: data?.email,
        telefono: data?.telefono,
        fuente: data?.fuente,
        proyectoInteres: data?.proyecto_interes,
        presupuesto: data?.presupuesto,
        stage: data?.stage,
        nivelInteres: data?.nivel_interes,
        ultimaActividad: data?.ultima_actividad,
        proximoSeguimiento: data?.proximo_seguimiento,
        notas: data?.notas,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
      };
    } catch (error) {
      console.error("Error creating lead:", error);
      throw error;
    }
  },

async createLeadWithAI(leadData, advisorId = null, extras = {}) {
  const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
  const anonKey =
    import.meta.env?.VITE_SUPABASE_ANON_KEY ||
    import.meta.env?.VITE_SUPABASE_PUBLISHABLE_KEY;

  if (!supabaseUrl) throw new Error("Falta VITE_SUPABASE_URL en el entorno");
  if (!anonKey) throw new Error("Falta VITE_SUPABASE_ANON_KEY (o publishable key) en el entorno");

  const lead = normalizeLeadPayload(leadData);

  const url = `${supabaseUrl}/functions/v1/qualify-lead`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: anonKey,
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify({
      lead,
      advisorId,
      // ✅ esto alimenta metadata de la notificación
      meta: {
        source_url: extras?.sourceUrl || null,
        utm_source: extras?.utm_source || null,
        utm_medium: extras?.utm_medium || null,
        utm_campaign: extras?.utm_campaign || null,
        utm_term: extras?.utm_term || null,
        utm_content: extras?.utm_content || null,
        campaign: extras?.campaign || null,
        page: extras?.page || "HeroSection",
      },
    }),
  });

  const raw = await res.text().catch(() => "");
  let parsed = null;
  try { parsed = raw ? JSON.parse(raw) : null; } catch { parsed = null; }

  if (!res.ok) {
    console.error("Edge error status:", res.status);
    console.error("Edge error raw body:", raw);
    console.error("Edge error parsed:", parsed);

    const detail = parsed?.pg
      ? ` | pg.code=${parsed.pg.code} | pg.message=${parsed.pg.message} | pg.details=${parsed.pg.details} | pg.hint=${parsed.pg.hint}`
      : "";

    throw new Error(`Edge Function error (${res.status}): ${parsed?.error || raw || "Unknown"}${detail}`);
  }

  return parsed ?? raw;
},


  // Update lead (panel autenticado)
  async updateLead(leadId, updates, advisorId) {
    try {
      const { data, error } = await supabase
        ?.from("crm_leads")
        ?.update({
          nombre: updates?.nombre,
          email: updates?.email,
          telefono: updates?.telefono,
          fuente: updates?.fuente,
          proyecto_interes: updates?.proyectoInteres,
          presupuesto: updates?.presupuesto,
          stage: updates?.stage,
          nivel_interes: updates?.nivelInteres,
          proximo_seguimiento: updates?.proximoSeguimiento,
          notas: updates?.notas,
        })
        ?.eq("id", leadId)
        ?.eq("advisor_id", advisorId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        advisorId: data?.advisor_id,
        nombre: data?.nombre,
        email: data?.email,
        telefono: data?.telefono,
        fuente: data?.fuente,
        proyectoInteres: data?.proyecto_interes,
        presupuesto: data?.presupuesto,
        stage: data?.stage,
        nivelInteres: data?.nivel_interes,
        ultimaActividad: data?.ultima_actividad,
        proximoSeguimiento: data?.proximo_seguimiento,
        notas: data?.notas,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
      };
    } catch (error) {
      console.error("Error updating lead:", error);
      throw error;
    }
  },

  // Delete lead (panel autenticado)
  async deleteLead(leadId, advisorId) {
    try {
      const { error } = await supabase
        ?.from("crm_leads")
        ?.delete()
        ?.eq("id", leadId)
        ?.eq("advisor_id", advisorId);

      if (error) throw error;
    } catch (error) {
      console.error("Error deleting lead:", error);
      throw error;
    }
  },

  // Subscribe realtime (panel autenticado)
  subscribeToLeads(advisorId, callback) {
    const subscription = supabase
      ?.channel("crm_leads_changes")
      ?.on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "crm_leads",
          filter: `advisor_id=eq.${advisorId}`,
        },
        (payload) => callback(payload)
      )
      ?.subscribe();

    return subscription;
  },

  unsubscribeFromLeads(subscription) {
    if (subscription) {
      supabase?.removeChannel(subscription);
    }
  },
};

export default crmService;
